import React, { useState, useEffect } from 'react';
import { Form } from '../form/form';
import styles from './clientForm.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import ClientCard from '../clientCard/clientCard';
import { Fieldset } from '../fieldSet/fieldSet';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '../button/button';
import { TextInput } from '../form/textInput';
import { ImageUpload } from '../imageUpload/imageUpload';
import { Quil } from '../form/quil';
import { SelectInput } from '../form/selectInput';
import { NestedDropdown } from 'components/nestedDropdown/nestedDropdown';
import dataService from '../../services/dataService';
import DOMPurify from 'dompurify';

const findClientById = (clients = [], id) => {
  if (!id) return null;

  for (const client of clients) {
    if (String(client.id) === String(id)) {
      return client;
    }

    const childMatch = findClientById(client.child_clients || [], id);
    if (childMatch) return childMatch;
  }

  return null;
};

// Remove the client being edited and all of its descendants. Selecting any of
// them as its parent would create a self-reference or circular hierarchy.
export const removeClientBranch = (clients = [], clientId) => {
  if (!clientId) return clients;

  return clients.reduce((options, client) => {
    if (String(client.id) === String(clientId)) {
      return options;
    }

    options.push({
      ...client,
      child_clients: removeClientBranch(client.child_clients || [], clientId),
    });
    return options;
  }, []);
};

export const ClientForm = (props) => {
  // Helper function to format textarea selection
  // Helper to apply formatting visually in contenteditable div
  const formatContentEditable = (field, command, value = null) => {
    const el = document.getElementById(field);
    if (!el) return;
    el.focus();
    document.execCommand(command, false, value);
  };

  // Sync contenteditable divs with react-hook-form

  // Handler for summary textarea
  const handleSummaryChange = (e) => {
    let value = e.target.value.replace(/<[^>]+>/g, '');
    if (value.length > 250) value = value.slice(0, 250);
    setSummaryValue(value);
    form.setValue('summary', value, { shouldValidate: true });
  };
  const {
    loading,
    clientData,
    allClients,
    yupSchema,
    isEdit,
    state,
    setState,
    clientRemove,
    accountStatus,
    accountStatusLoading,
  } = props;
  const clientUsage = accountStatus?.client_usage;
  const cannotCreateClient = !isEdit && clientUsage && !clientUsage.can_create_client;
  const blockedFromCreating = !isEdit && cannotCreateClient;

  const [initialValues] = useState(() => {
    return dataService.parseFormDataForClientEdit(clientData);
  });

  const [parentId, setParentId] = useState(clientData?.parent_id || null); // Tracks selected `parent_id`
  const [imageValidationError, setImageValidationError] = useState('');
  const [oversizedImageIndexes, setOversizedImageIndexes] = useState([]);

  // Update parentId when clientData changes (important for initial load)
  useEffect(() => {
    if (clientData) {
      setParentId(clientData.parent_id);
    }
  }, [clientData]);

  const form = useForm({
    resolver: yupResolver(yupSchema),
    mode: 'all',
    defaultValues: {
      ...clientData,
      parent_id: clientData?.parent_id || null,
      client_type_id: clientData?.parent_id ? 7 : 6, // Default client_type_id based on parent_id
      ...initialValues,
    },
  });
  const [summaryValue, setSummaryValue] = useState(form.getValues('summary') || '');
  const [summaryCharCount, setSummaryCharCount] = useState(0);
  const parentOptions = removeClientBranch(allClients, clientData?.id);

  // Toolbar actions for summary (match description logic)
  const formatSummary = (command) => {
    const el = document.getElementById('summary');
    if (!el) return;
    el.focus();
    document.execCommand(command, false, null);
  };

  const handleTypeChange = (value, label) => {
    setParentId(value); // Update parent_id
    form.setValue('parent_id', value); // Update parent_id in form state
    form.setValue('client_type_id', value ? 7 : 6); // Dynamically update client_type_id
  };

  // Clear validation errors when images change
  useEffect(() => {
    if (imageValidationError) {
      setImageValidationError('');
      setOversizedImageIndexes([]);
    }
  }, [state.images]);

  // Initialize YouTube and Vimeo contentEditable fields
  useEffect(() => {
    // Initialize YouTube field
    const youtubeValue = form.getValues('youtube_url') || '';
    const youtubeElement = document.getElementById('youtube_url');
    if (youtubeElement && youtubeValue) {
      youtubeElement.innerHTML = youtubeValue;
    }

    // Initialize Vimeo field
    const vimeoValue = form.getValues('vimeo_url') || '';
    const vimeoElement = document.getElementById('vimeo_url');
    if (vimeoElement && vimeoValue) {
      vimeoElement.innerHTML = vimeoValue;
    }
  }, []);

  // Prepopulate summary, description, youtube, vimeo fields if data exists
  useEffect(() => {
    if (form && clientData) {
      if (clientData.summary) {
        setSummaryValue(clientData.summary);
        setSummaryCharCount(
          DOMPurify.sanitize(clientData.summary).replace(/<[^>]+>/g, '').length
        );
        form.setValue('summary', clientData.summary);
        const summaryElement = document.getElementById('summary');
        if (summaryElement) summaryElement.innerHTML = DOMPurify.sanitize(clientData.summary);
      }
      if (clientData.description) {
        form.setValue('description', clientData.description);
        const descElement = document.getElementById('description');
        if (descElement) descElement.innerHTML = DOMPurify.sanitize(clientData.description);
      }
      if (clientData.youtube_url) {
        form.setValue('youtube_url', clientData.youtube_url);
        const ytElement = document.getElementById('youtube_url');
        if (ytElement) ytElement.innerHTML = DOMPurify.sanitize(clientData.youtube_url);
      }
      if (clientData.vimeo_url) {
        form.setValue('vimeo_url', clientData.vimeo_url);
        const vimeoElement = document.getElementById('vimeo_url');
        if (vimeoElement) vimeoElement.innerHTML = DOMPurify.sanitize(clientData.vimeo_url);
      }
    }
  }, [form, clientData]);

  const handleSubmitForm = (values) => {
    // Reset previous validation errors
    setOversizedImageIndexes([]);

    // Validate image sizes (2MB = 2 * 1024 * 1024 bytes)
    const maxSize = 2 * 1024 * 1024;
    const oversizedIndexes = [];

    if (state.images && state.images.length > 0) {
      state.images.forEach((image, index) => {
        // Check if image has originalSize property
        if (image.originalSize && image.originalSize > maxSize) {
          oversizedIndexes.push(index);
        }
      });
    }

    if (oversizedIndexes.length > 0) {
      setOversizedImageIndexes(oversizedIndexes);
      setImageValidationError(`Error: ${oversizedIndexes.length} image(s) exceed 2MB. Please choose smaller files.`);
      return; // Prevent form submission
    }

    if (cannotCreateClient) {
      return;
    }

    // client_type is a read-only association returned by the API. Only send
    // client_type_id so an old nested object cannot conflict with the selected
    // value.
    const body = { ...values, parent_id: parentId };
    delete body.client_type;

    props.createClient({ body }); // Submit with parent_id
  };

  const action = [
    <Button
      className="btn"
      type="submit"
      id="clientSubmitBtn"
      submit
      inline
      disabled={loading || accountStatusLoading || blockedFromCreating}
    >
      {isEdit ? 'Save changes' : 'Create client profile'}
    </Button>,
  ];

  if (isEdit) {
    action.push(
      <Button
        style={{ float: 'left' }}
        destroy
        align="left"
        type="button"
        inline
        onClick={() => {
          clientRemove(clientData);
        }}
      >
        Delete client
      </Button>,
    );
  }

  const handleContentChange = (field, e) => {
    let html = e.currentTarget.innerHTML;
    // Sanitize and strip HTML tags for accurate character count
    const sanitized = DOMPurify.sanitize(html);
    const temp = document.createElement('div');
    temp.innerHTML = sanitized;
    let text = temp.innerText || temp.textContent || '';
    if (field === 'summary') {
      if (text.length > 250) {
        // Prevent further input if at limit
        const prev = summaryValue || '';
        e.currentTarget.innerHTML = prev;
        // Move caret to end
        const el = e.currentTarget;
        const range = document.createRange();
        const sel = window.getSelection();
        let lastTextNode = null;
        function findLastTextNode(node) {
          if (node.nodeType === Node.TEXT_NODE) lastTextNode = node;
          for (let i = 0; i < node.childNodes.length; i++) {
            findLastTextNode(node.childNodes[i]);
          }
        }
        findLastTextNode(el);
        if (lastTextNode) {
          range.setStart(lastTextNode, lastTextNode.length);
          range.collapse(true);
        } else {
          range.selectNodeContents(el);
          range.collapse(false);
        }
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }
      setSummaryValue(html);
      setSummaryCharCount(text.length);
      form.setValue('summary', html, { shouldValidate: true });
    } else {
      form.setValue(field, html, { shouldValidate: true });
    }
  };

  return (
    <div>
      <FormProvider {...form}>
        <Form
          onSubmit={form.handleSubmit((values) => {
            handleSubmitForm(values);
          })}
          className={styles.clientFormContainer}
          actions={[<div className="action-footer">{action}</div>]}
        >
          {cannotCreateClient && (
            <div className={styles.accountLimitNotice}>
              Your account has reached its client limit.
            </div>
          )}
          <div className="row g-0">
            <div className="col-lg-4 col-12 mx-0 px-0">
              <ClientCard
                client={{
                  ...initialValues,
                  client_images: state.images || initialValues.client_images || []
                }}
                isEditable={true}
                standalone={false}
                state={state}
              />
            </div>
            <div className="col-lg-8 p-0">
              <Fieldset
                title="Hotel details"

                description="The client profile is displayed on all job advert posts when a job is posted to Holedo."
              >
                <div className="row">
                  <div className="col-lg-6">
                    <TextInput label="Name of company" type="text" required={true} name="title" />
                  </div>
                  <div className="col-lg-6">
                    <div className="form-group mt-sm-3 mt-2" style={{ clear: 'both' }}>
                      <label className={styles.fieldLabel}>Parent Company</label>
                      <NestedDropdown
                        clients={parentOptions}
                        selectedClient={findClientById(parentOptions, clientData?.parent_id)}
                        defaultLabel={clientData && !clientData.parent_id ? 'Stand Alone Hotel' : 'Select Parent Company'}
                        onSelection={(value, label) => handleTypeChange(value, label)}
                      />
                    </div>
                  </div>
                </div>
                <ImageUpload
                  state={state}
                  setState={setState}
                  form={form}
                  isEdit={isEdit}
                  changeFunc={props.updateClient}
                  name="client_images"
                  label="Upload photos of the hotel"
                  description="Upload images of the hotel to display on your job posting when you post a job for this client.
Upload <strong>5 images</strong> to enable the gallery view on your job posts. Please ensure all images are smaller than 2MB."
                  validationError={imageValidationError}
                  oversizedImages={oversizedImageIndexes}
                >
                  <Button
                    submit
                    inline
                    size="small"
                  >
                    Upload photo
                  </Button>
                </ImageUpload>


                {/* Summary field with its own toolbar and contenteditable */}
                <div className={styles.QuillEditor + ' my-4'} style={{ clear: 'both' }}>
                  <label className={styles.clientDescriptionLabel} htmlFor="summary">
                    <small className="text-danger">* </small>
                    Hotel Summary
                    <span className="d-block mb-3" style={{ fontWeight: 'normal', color: '#7c8a9c', fontSize: '14px', marginTop: '2px' }}>This will be displayed on the company page</span>
                  </label>
                  <div className={styles.formatToolbar} style={{ marginBottom: 8 }}>
                    <button type="button" className={styles.formatButton} style={{ fontWeight: 'bold' }} onClick={() => formatSummary('bold')}>B</button>
                    <button type="button" className={styles.formatButton} style={{ fontStyle: 'italic' }} onClick={() => formatSummary('italic')}>I</button>
                    <button type="button" className={styles.formatButton} style={{ textDecoration: 'underline' }} onClick={() => formatSummary('underline')}>U</button>
                    <button type="button" className={styles.formatButton} onClick={() => formatSummary('insertOrderedList')}>1.</button>
                    <button type="button" className={styles.formatButton} onClick={() => formatSummary('insertUnorderedList')}>&bull;</button>
                  </div>
                  <div
                    className={styles.textareaBox}
                    name="summary"
                    id="summary"
                    contentEditable
                    suppressContentEditableWarning
                    style={{ minHeight: 80, maxHeight: 200, overflowY: 'auto', outline: 'none', whiteSpace: 'pre-wrap' }}
                    onInput={e => handleContentChange('summary', e)}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 14, color: '#888', marginTop: 4 }}>
                    {summaryCharCount}/250 characters
                  </div>
                  <small className="text-danger float-start" style={{ clear: 'both' }}>
                    {form.formState.errors.summary?.message}
                  </small>
                </div>


                {/* Description field with its own toolbar and contenteditable */}
                <div className={styles.QuillEditor + ' my-4'} style={{ clear: 'both' }}>
                  <label className={styles.clientDescriptionLabel} htmlFor="description">
                    <small className="text-danger">* </small>
                    Hotel Description
                    <span className="d-block mb-3" style={{ fontWeight: 'normal', color: '#7c8a9c', fontSize: '14px', marginTop: '2px' }}>A paragraph or two about the hotel. This will be displayed on all job adverts posted. This will be displayed on the company page</span>
                  </label>
                  <div className={styles.formatToolbar} style={{ marginBottom: 8 }}>
                    <button type="button" className={styles.formatButton} style={{ fontWeight: 'bold' }} onClick={() => formatContentEditable('description', 'bold')}>B</button>
                    <button type="button" className={styles.formatButton} style={{ fontStyle: 'italic' }} onClick={() => formatContentEditable('description', 'italic')}>I</button>
                    <button type="button" className={styles.formatButton} style={{ textDecoration: 'underline' }} onClick={() => formatContentEditable('description', 'underline')}>U</button>
                    <button type="button" className={styles.formatButton} onClick={() => formatContentEditable('description', 'insertOrderedList')}>1.</button>
                    <button type="button" className={styles.formatButton} onClick={() => formatContentEditable('description', 'insertUnorderedList')}>&bull;</button>
                  </div>
                  <div
                    className={styles.textareaBox}
                    name="description"
                    id="description"
                    contentEditable
                    suppressContentEditableWarning
                    style={{ minHeight: 80, maxHeight: 200, overflowY: 'auto', outline: 'none', whiteSpace: 'pre-wrap' }}
                    onInput={e => handleContentChange('description', e)}
                  />
                  {/* Character count removed for description */}
                  <small className="text-danger float-start" style={{ clear: 'both' }}>
                    {form.formState.errors.description?.message}
                  </small>
                </div>
                {/* YouTube field with its own toolbar and contenteditable */}
                <div className={styles.QuillEditor + ' my-4'} style={{ clear: 'both' }}>
                  <label className={styles.clientDescriptionLabel} htmlFor="youtube_url">
                    Youtube
                    <span className="d-block mb-3" style={{ fontWeight: 'normal', color: '#7c8a9c', fontSize: '14px', marginTop: '2px' }}>Paste your youtube URL.</span>
                  </label>
                  <div className={styles.formatToolbar} style={{ marginBottom: 8 }}>
                    <button type="button" className={styles.formatButton} style={{ fontWeight: 'bold' }} onClick={() => formatContentEditable('youtube_url', 'bold')}>B</button>
                    <button type="button" className={styles.formatButton} style={{ fontStyle: 'italic' }} onClick={() => formatContentEditable('youtube_url', 'italic')}>I</button>
                    <button type="button" className={styles.formatButton} style={{ textDecoration: 'underline' }} onClick={() => formatContentEditable('youtube_url', 'underline')}>U</button>
                    <button type="button" className={styles.formatButton} onClick={() => formatContentEditable('youtube_url', 'insertOrderedList')}>1.</button>
                    <button type="button" className={styles.formatButton} onClick={() => formatContentEditable('youtube_url', 'insertUnorderedList')}>&bull;</button>
                  </div>
                  <div
                    className={styles.textareaBox}
                    name="youtube_url"
                    id="youtube_url"
                    contentEditable
                    suppressContentEditableWarning
                    style={{ minHeight: 80, maxHeight: 200, overflowY: 'auto', outline: 'none', whiteSpace: 'pre-wrap' }}
                    onInput={e => handleContentChange('youtube_url', e)}
                  />
                  <small className="text-danger float-start" style={{ clear: 'both' }}>
                    {form.formState.errors.youtube_url?.message}
                  </small>
                </div>

                {/* Vimeo field with its own toolbar and contenteditable */}
                <div className={styles.QuillEditor + ' my-4'} style={{ clear: 'both' }}>
                  <label className={styles.clientDescriptionLabel} htmlFor="vimeo_url">
                    Vimeo
                    <span className="d-block mb-3" style={{ fontWeight: 'normal', color: '#7c8a9c', fontSize: '14px', marginTop: '2px' }}>Paste your vimeo URL.</span>
                  </label>
                  <div className={styles.formatToolbar} style={{ marginBottom: 8 }}>
                    <button type="button" className={styles.formatButton} style={{ fontWeight: 'bold' }} onClick={() => formatContentEditable('vimeo_url', 'bold')}>B</button>
                    <button type="button" className={styles.formatButton} style={{ fontStyle: 'italic' }} onClick={() => formatContentEditable('vimeo_url', 'italic')}>I</button>
                    <button type="button" className={styles.formatButton} style={{ textDecoration: 'underline' }} onClick={() => formatContentEditable('vimeo_url', 'underline')}>U</button>
                    <button type="button" className={styles.formatButton} onClick={() => formatContentEditable('vimeo_url', 'insertOrderedList')}>1.</button>
                    <button type="button" className={styles.formatButton} onClick={() => formatContentEditable('vimeo_url', 'insertUnorderedList')}>&bull;</button>
                  </div>
                  <div
                    className={styles.textareaBox}
                    name="vimeo_url"
                    id="vimeo_url"
                    contentEditable
                    suppressContentEditableWarning
                    style={{ minHeight: 80, maxHeight: 200, overflowY: 'auto', outline: 'none', whiteSpace: 'pre-wrap' }}
                    onInput={e => handleContentChange('vimeo_url', e)}
                  />
                  <small className="text-danger float-start" style={{ clear: 'both' }}>
                    {form.formState.errors.vimeo_url?.message}
                  </small>
                </div>
              </Fieldset>
              <Fieldset title="Hotel address">
                <div className={styles.addressContainer}>
                  <div className="row">
                    <div className=" col-lg-6">
                      <TextInput
                        type="text"
                        label="Address"
                        required={true}
                        name="address_line_1"
                        placeHolder="Address line 1"
                      />
                      <TextInput
                        type="text"
                        required={true}
                        name="address_line_2"
                        placeHolder="Address line 2"
                      />
                      <TextInput
                        type="text"
                        required={true}
                        name="address_line_3"
                        placeHolder="Address line 3"
                      />
                      <TextInput
                        type="text"
                        label="State, province or region"
                        name="state"
                      />
                    </div>
                    <div className="col-lg-6">
                      <div className={styles.addressContainer}>
                        <TextInput type="text" label="Location" required={true} name="city" />
                        <div className="row">
                          <div className="col-6 col-lg-8">
                            <SelectInput
                              name="country_id"
                              label="Country"
                              options={props.countryOptions}
                              mode="single"
                              required={true}
                              searchable={true}
                            />
                          </div>
                          <div className="col-lg-4 col-6">
                            <TextInput
                              type="text"
                              label="Area Code"
                              name="area_code"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Fieldset>
            </div>
          </div>
        </Form>
      </FormProvider>
    </div>
  );
};
